import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield,
  Database,
  TrendingUp,
  Plus,
  DollarSign,
  ChevronRight,
  Sparkles,
  Zap,
  FileText,
  Loader2,
  Lightbulb,
  Store,
} from "lucide-react";
import { categoryIcons } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const DashboardPage = () => {
  const api = useApi();
  const { user, fetchUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketDemand, setMarketDemand] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [statsRes, demandRes, recsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/market-demand"),
        api.get("/analytics/recommendations"),
      ]);
      setStats(statsRes.data);
      setMarketDemand(demandRes.data);
      setRecommendations(recsRes.data);
      fetchUser();
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [api, fetchUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Welcome back, {user?.name?.split(" ")[0]}!</h1>
          <p className="text-gray-400 mt-1">Here&apos;s an overview of your data portfolio</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            {
              label: "Data Items",
              value: stats?.total_data_items || 0,
              icon: Database,
              color: "from-purple-500 to-violet-600",
            },
            {
              label: "Total Value",
              value: `$${(stats?.total_estimated_value || 0).toFixed(2)}`,
              icon: DollarSign,
              color: "from-cyan-500 to-blue-600",
            },
            {
              label: "For Sale",
              value: stats?.items_for_sale || 0,
              icon: TrendingUp,
              color: "from-violet-500 to-purple-600",
            },
            {
              label: "Valuations",
              value: stats?.recent_valuations || 0,
              icon: Sparkles,
              color: "from-blue-500 to-cyan-500",
            },
            {
              label: "Privacy Score",
              value: stats?.privacy_score || 75,
              icon: Shield,
              color: stats?.privacy_score >= 70 ? "from-green-500 to-emerald-600" : "from-yellow-500 to-orange-600",
            },
          ].map((stat, i) => (
            <div key={stat.label} className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/20" data-testid={`stat-${i}`}>
              <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-3`}>
                <stat.icon className="w-4 h-4 text-white" />
              </div>
              <p className="text-xl font-bold text-white">{stat.value}</p>
              <p className="text-xs text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions & AI Recommendations */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Quick Actions */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/vault")}
                className="w-full flex items-center justify-between p-4 bg-purple-500/10 rounded-xl hover:bg-purple-500/20 transition border border-purple-500/20"
                data-testid="quick-add-data"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-violet-600 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-white">Add New Data</p>
                    <p className="text-sm text-gray-400">Upload data to your vault</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => navigate("/valuation")}
                className="w-full flex items-center justify-between p-4 bg-cyan-500/10 rounded-xl hover:bg-cyan-500/20 transition border border-cyan-500/20"
                data-testid="quick-valuation"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-white">AI Valuation</p>
                    <p className="text-sm text-gray-400">Get insights on data value</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => navigate("/market")}
                className="w-full flex items-center justify-between p-4 bg-violet-500/10 rounded-xl hover:bg-violet-500/20 transition border border-violet-500/20"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Store className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-white">Marketplace</p>
                    <p className="text-sm text-gray-400">Browse or sell data</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
              <h2 className="text-lg font-semibold text-white">AI Recommendations</h2>
            </div>
            <div className="space-y-3">
              {recommendations.length === 0 ? (
                <p className="text-gray-500 text-sm">Add data to get personalized recommendations</p>
              ) : (
                recommendations.map((rec) => (
                  <div key={rec.title} className="p-4 bg-white/5 rounded-xl border border-purple-500/10">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        rec.priority === 'high' ? 'bg-red-500/20' : rec.priority === 'medium' ? 'bg-yellow-500/20' : 'bg-gray-500/20'
                      }`}>
                        {rec.type === 'collect' && <Plus className="w-4 h-4 text-green-400" />}
                        {rec.type === 'sell' && <DollarSign className="w-4 h-4 text-cyan-400" />}
                        {rec.type === 'protect' && <Shield className="w-4 h-4 text-purple-400" />}
                        {rec.type === 'valuate' && <Sparkles className="w-4 h-4 text-yellow-400" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-white text-sm">{rec.title}</p>
                        <p className="text-gray-400 text-xs mt-1">{rec.description}</p>
                        {rec.potential_value && (
                          <p className="text-cyan-400 text-xs mt-2">Potential: ${rec.potential_value}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Market Demand */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Market Demand</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {marketDemand.slice(0, 4).map((item) => {
              const Icon = categoryIcons[item.category] || FileText;
              return (
                <div key={item.category} className="p-3 bg-white/5 rounded-xl border border-purple-500/10">
                  <div className="flex items-center justify-between mb-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    {item.trending && <Zap className="w-3 h-3 text-yellow-400" />}
                  </div>
                  <span className="capitalize text-white text-sm">{item.category}</span>
                  <span
                    className={`block mt-1 text-xs font-medium ${
                      item.demand_level === "Very High"
                        ? "text-green-400"
                        : item.demand_level === "High"
                        ? "text-cyan-400"
                        : "text-gray-400"
                    }`}
                  >
                    {item.demand_level}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Category Breakdown */}
        {stats?.category_breakdown && Object.keys(stats.category_breakdown).length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
            <h2 className="text-lg font-semibold text-white mb-4">Your Data by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.category_breakdown).map(([category, data]) => {
                const Icon = categoryIcons[category] || FileText;
                return (
                  <div key={category} className="p-4 bg-gradient-to-br from-purple-500/10 to-cyan-500/10 rounded-xl border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-purple-400" />
                      <span className="capitalize font-medium text-gray-300">{category}</span>
                    </div>
                    <p className="text-xl font-bold text-white">{data.count} items</p>
                    <p className="text-sm text-gray-400">${data.value.toFixed(2)}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Continue in Part 2...

// Data Vault Page
